import { useEffect, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { TabEditor } from "@/components/ui/legal/TabEditor";
import {
  editTypesEnum,
  type ItemsType,
  type EditTypesEnum,
  type LegalAttributeType,
} from "@/types/entities/legal";
import RichTextEditor from "@/components/common/editor";
import {
  useCreateTermsConditions,
  useUpdateTermsConditions,
} from "@/queries/legal";
import { useLegalStore } from "@/stores/legal/useLegalStore";
import { formatToYMD } from "@/utils/dates";
import { cleanExtraLineBreaks, isHtmlEmpty } from "@/utils/format";
import { useToast } from "@elaniintc/goes-design-system";

interface EditDataTermsProps {
  onClose: () => void;
}

export default function EditDataTerms({ onClose }: EditDataTermsProps) {
  const [editionType, setEditionType] = useState<EditTypesEnum>("visual");
  const { selected, isValid } = useLegalStore();
  const [debouncedContent] = useDebounceValue(selected.content, 500);
  const [hasNoContent, setHasNocontent] = useState(false);
  const { showToast } = useToast();

  const { mutateAsync: createTermsConditions, isPending: isCreateLoading } =
    useCreateTermsConditions({});

  const { mutateAsync: updateTermsConditions, isPending: isUpdateLoading } =
    useUpdateTermsConditions({});

  useEffect(() => {
    setHasNocontent(isHtmlEmpty(debouncedContent));
  }, [debouncedContent]);

  const handleSave = async () => {
    let isSameDate = false;
    let isUpdate = false;
    if (isHtmlEmpty(selected.content)) return;
    try {
      if ("id" in selected) {
        const typedSelected = selected as LegalAttributeType;
        const registerDate = new Date(typedSelected.created_at);
        const today = new Date();

        const formattedRegisterDate = formatToYMD(registerDate);
        const formattedToday = formatToYMD(today);
        isSameDate = formattedRegisterDate === formattedToday;
        isUpdate = true;
        if (isSameDate) {
          await updateTermsConditions({
            ...typedSelected,
            content: cleanExtraLineBreaks(typedSelected.content),
          });
        } else
          await createTermsConditions({
            content: cleanExtraLineBreaks(selected.content),
          });
      } else
        await createTermsConditions({
          content: cleanExtraLineBreaks(selected.content),
        });
      showToast({
        title:
          "Términos y condiciones" + (isUpdate ? " actualizados" : " creados"),
        description: `Los términos y condiciones han sido ${isUpdate ? "actualizados" : "creados"} correctamente.`,
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch {
      showToast({
        title: `Error al ${isUpdate ? "actualizar" : "crear"} los términos y condiciones`,
        description: `Ha ocurrido un error al ${isUpdate ? "actualizar" : "crear"} los términos y condiciones.`,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
    onClose();
  };

  const editItems: ItemsType[] = useMemo(
    () => [
      {
        label: "Editor Visual",
        onClick: () => setEditionType(editTypesEnum.visual),
        isSelected: editionType === editTypesEnum.visual,
      },
      {
        label: "Editor HTML",
        onClick: () => setEditionType(editTypesEnum.html),
        isSelected: editionType === editTypesEnum.html,
      },
    ],
    [editionType],
  );

  return (
    <div className="bg-neutral-object w-full h-full border border-neutral shadow-custom rounded-xl">
      <TabEditor
        items={editItems}
        onClose={onClose}
        onSave={handleSave}
        isLoading={isCreateLoading || isUpdateLoading}
        isDisabled={
          hasNoContent || (!isValid && editionType === editTypesEnum.html)
        }
      >
        <div className="relative overflow-x-hidden overflow-y-hidden bg-neutral-object-accent-silent border-t border-neutral-quiet ">
          <RichTextEditor editionType={editionType} />
        </div>
      </TabEditor>
    </div>
  );
}
